export interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  tags: string[];
  featured: boolean;
}

export const articles: Article[] = [
  {
    id: '1',
    title: '费曼技巧：用最简单的语言解释最复杂的概念',
    excerpt: '诺贝尔物理学奖得主理查德·费曼的独门学习法，教你如何通过"教给别人"来真正掌握知识。',
    content: `<p>在所有的学习方法中，费曼技巧可能是被提及最多、但真正掌握的人最少的一种。这个以诺贝尔物理学奖得主理查德·费曼命名的学习方法，核心原理出奇地简单：<strong>如果你不能用简单的语言解释清楚一个概念，那说明你还没有真正理解它。</strong></p>

<h2>费曼技巧的四步法</h2>

<p>第一步：<strong>选择一个概念</strong>。拿出一张白纸，在顶部写下你想要学习的概念名称。</p>

<p>第二步：<strong>教给一个孩子</strong>。用最平实、最简单的语言解释这个概念，假设你正在教一个完全不了解这个领域的人。避免使用专业术语，如果必须使用，先解释清楚。</p>

<p>第三步：<strong>找到知识缺口</strong>。在解释过程中，你会发现有些地方说不清楚、卡住了。这些就是你真正没理解的地方。回到原始材料重新学习，直到能用简单语言解释清楚。</p>

<p>第四步：<strong>简化并类比</strong>。将你的解释进一步精炼，寻找生活中常见的类比来帮助理解。好的类比是知识的桥梁。</p>

<h2>为什么费曼技巧如此有效？</h2>

<p>费曼技巧之所以有效，是因为它强迫你从"识别"层面进入"理解"层面。大多数人的学习停留在"哦我懂了"的阶段，但实际上只是<strong>认出了信息</strong>，而非真正内化了知识。</p>

<p>当你试图用简单语言解释时，你被迫建立起概念之间的联系，发现逻辑漏洞，这正是深度学习发生的地方。</p>

<h2>实战应用：如何在日常学习中运用费曼技巧</h2>

<p>不必等到完整学完一个章节才开始。每学完一个小节，停下来，合上书，用自己的话复述一遍核心内容。录下来听回放，看看自己能不能听懂。不能？那就回去再学。</p>

<p>这个方法对于准备考试、学习新技能、甚至准备演讲都极其有效。记住费曼的那句话：<strong>"如果你不能简单地解释它，你就没有充分地理解它。"</strong></p>`,
    category: 'hexin-fangfa',
    author: '秘密教学编辑部',
    date: '2026-08-08',
    readTime: '8分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20Feynman%20technique%20learning%20method%2C%20warm%20amber%20tones%2C%20person%20explaining%20complex%20concept%20with%20simple%20diagrams%2C%20clean%20educational%20illustration%20style%2C%20soft%20gradient%20background%2C%20no%20text&width=1200&height=800&seq=article-1&orientation=landscape',
    tags: ['费曼技巧', '学习心法', '核心方法'],
    featured: true
  },
  {
    id: '2',
    title: '间隔重复：大脑记忆的科学密码',
    excerpt: '遗忘曲线不是你的敌人，掌握间隔重复的节奏，让记忆效率提升300%',
    content: `<p>如果你花3小时一口气背完所有单词，和分6次、每次30分钟来背，哪个效果更好？答案可能会让你惊讶：<strong>后者在前者基础上，长期记忆留存率可高出200%-300%。</strong>这就是间隔重复的力量。</p>

<h2>艾宾浩斯遗忘曲线的真相</h2>

<p>19世纪末，德国心理学家赫尔曼·艾宾浩斯通过大量实验发现了人类记忆的遗忘规律：<strong>学习后的20分钟内，42%的内容被遗忘；1小时后，56%被遗忘；一天后，74%被遗忘。</strong></p>

<p>但这不是坏消息——恰恰相反，遗忘曲线揭示了最佳复习时机的秘密。在即将遗忘但尚未完全遗忘的时刻进行复习，记忆强化效果最佳。</p>

<h2>黄金复习间隔</h2>

<p>基于大量研究，最佳复习间隔为：</p>
<ul>
<li>第一次复习：学习后10分钟</li>
<li>第二次复习：学习后1天</li>
<li>第三次复习：学习后3天</li>
<li>第四次复习：学习后7天</li>
<li>第五次复习：学习后30天</li>
</ul>

<p>每次复习不需要很长时间，5-10分钟快速回顾核心要点即可。关键是<strong>在正确的时机进行复习</strong>。</p>

<h2>现代工具推荐</h2>

<p>Anki（暗记）是目前最流行的间隔重复软件，基于SM-2算法自动计算每张卡片的复习时间。你也可以使用Quizlet、RemNote等工具来实现间隔重复。但核心不在于工具，而在于<strong>坚持按照算法推荐的时间进行复习</strong>。</p>`,
    category: 'yincang-jiqiao',
    author: '秘密教学编辑部',
    date: '2026-08-06',
    readTime: '10分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20spaced%20repetition%20and%20memory%20science%2C%20warm%20terracotta%20tones%2C%20abstract%20curves%20representing%20forgetting%20curve%2C%20clean%20educational%20art%20style%2C%20soft%20gradient%20background%2C%20no%20text&width=1200&height=800&seq=article-2&orientation=landscape',
    tags: ['间隔重复', '记忆科学', '学习技巧'],
    featured: true
  },
  {
    id: '3',
    title: '主动回忆：为什么反复阅读是最低效的学习方式',
    excerpt: '揭开"看起来很努力"的学习假象，掌握真正有效的主动回忆法',
    content: `<p>打开课本，从头读到尾，用荧光笔划满重点，然后觉得自己学了很多——<strong>这是最多人使用、但效率最低的学习方式。</strong>认知科学研究表明：单纯的重复阅读（被动学习）几乎是浪费时间。</p>

<h2>被动学习的陷阱</h2>

<p>当你反复阅读同样的内容时，你会产生一种"流畅性错觉"——因为内容越来越熟悉，你误以为自己已经掌握了。但实际上，你只是在<strong>强化识别能力</strong>，而非回忆能力。</p>

<p>考试和实际应用需要的恰恰是后者：在没有提示的情况下，从大脑中提取信息。</p>

<h2>什么是主动回忆？</h2>

<p>主动回忆是指：<strong>不看书本或笔记，主动从大脑中提取学过的信息。</strong>具体做法包括：</p>
<ul>
<li>合上书，默写刚学内容的关键要点</li>
<li>做练习题时不翻看答案或参考书</li>
<li>在纸上画出知识点的思维导图（不参考任何材料）</li>
<li>向他人复述学到的内容</li>
<li>使用闪卡进行自测</li>
</ul>

<h2>主动回忆的科学依据</h2>

<p>2011年发表在《科学》杂志上的一项研究发现：<strong>进行一次主动回忆测试的学生，在一周后的测试中比反复阅读的学生高出50%的成绩。</strong>更惊人的是，即使主动回忆组的反馈被延迟，效果仍然显著优于反复阅读组。</p>

<p>这是因为每次成功回忆都在<strong>强化神经通路</strong>，让未来提取信息变得更加容易。这就像是给大脑的记忆通路铺设高速公路，而不是乡间小路。</p>`,
    category: 'hexin-fangfa',
    author: '秘密教学编辑部',
    date: '2026-08-04',
    readTime: '7分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20active%20recall%20learning%20method%2C%20warm%20golden%20tones%2C%20person%20thinking%20deeply%20without%20looking%20at%20book%2C%20abstract%20brain%20visualization%2C%20clean%20educational%20art%2C%20no%20text&width=1200&height=800&seq=article-3&orientation=landscape',
    tags: ['主动回忆', '学习心法', '认知科学'],
    featured: true
  },
  {
    id: '4',
    title: '知识迁移：如何将一门学科的方法应用到另一领域',
    excerpt: '高手的学习秘密不在于学得多，而在于"迁移"得快',
    content: `<p>为什么有些人学什么都快？他们似乎能够轻松地将一个领域的知识应用到另一个完全不同的领域。这不是天赋，而是一种可以训练的能力：<strong>知识迁移。</strong></p>

<h2>什么是知识迁移？</h2>

<p>知识迁移指的是将一个领域学到的知识、技能或思维方式应用到另一个领域的能力。它是<strong>学习的最高境界</strong>——超越了简单的记忆和理解，达到了应用和创造的水平。</p>

<h2>迁移的三个层次</h2>

<p><strong>低路迁移：</strong>在相似情境下自动应用已有技能。例如学会了开车后，开不同的车也能很快上手。</p>

<p><strong>高路迁移：</strong>有意识地从过去经验中抽象出原则，应用到新情境。例如将下棋的策略思维应用到商业决策中。</p>

<p><strong>创造性迁移：</strong>将不同领域的知识融合，创造出全新的解决方案。这是最高层次的迁移，也是创新的源泉。</p>

<h2>如何训练知识迁移能力？</h2>

<p>第一，<strong>抽象化训练</strong>：每学完一个知识点，问自己"这个原理还可以用在哪里？"</p>

<p>第二，<strong>跨领域阅读</strong>：不要只读专业领域的书。物理学家的思维方式可能启发你的商业策略，生物学家的观察方法可能优化你的产品设计。</p>

<p>第三，<strong>教授他人</strong>：当你尝试向不同背景的人解释一个概念时，你被迫寻找共通的理解框架，这正是迁移能力的核心。</p>`,
    category: 'gaoxiao-chuanshou',
    author: '秘密教学编辑部',
    date: '2026-08-02',
    readTime: '9分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20knowledge%20transfer%20across%20disciplines%2C%20warm%20sienna%20tones%2C%20bridges%20connecting%20different%20knowledge%20domains%2C%20abstract%20flow%20design%2C%20clean%20educational%20art%2C%20no%20text&width=1200&height=800&seq=article-4&orientation=landscape',
    tags: ['知识迁移', '跨领域', '高效传授'],
    featured: false
  },
  {
    id: '5',
    title: '思维模型：查理·芒格的多元思维框架解密',
    excerpt: '掌握100个思维模型，你就拥有了看透世界本质的眼镜',
    content: `<p>查理·芒格——巴菲特最重要的合伙人——有一个著名的学习方法：<strong>掌握多元思维模型。</strong>他认为，只用一种思维模型看世界，就像一个拿着锤子的人，看到什么都像钉子。</p>

<h2>什么是思维模型？</h2>

<p>思维模型是对世界运行规律的简化和抽象。它们是从各个学科中提炼出的<strong>核心原理和思维框架</strong>。例如：</p>
<ul>
<li>复利效应（数学/金融）</li>
<li>反脆弱（统计学/哲学）</li>
<li>第一性原理（物理学）</li>
<li>供需关系（经济学）</li>
<li>进化论（生物学）</li>
<li>系统思维（工程学）</li>
</ul>

<h2>为什么需要多元思维模型？</h2>

<p>单一学科的思维模型会让你陷入<strong>"铁锤人倾向"</strong>——用你唯一会的工具去解决所有问题。而拥有80-90个重要思维模型，你就能从多个角度审视同一个问题，做出更准确的判断。</p>

<h2>如何建立你的思维模型库？</h2>

<p>芒格建议从硬科学开始——数学、物理学、生物学、心理学。这些学科中的核心原理经过了最严格的验证。每掌握一个思维模型，就想一想：<strong>这个模型如何解释我生活中的某个现象？如何帮助我做出更好的决策？</strong></p>

<p>思维模型的精髓不在于记忆定义，而在于<strong>内化为直觉</strong>。当你能在遇到问题时自动调出正确的思维模型，你就真正掌握了它。</p>`,
    category: 'jingying-celve',
    author: '秘密教学编辑部',
    date: '2026-07-30',
    readTime: '11分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20mental%20models%20and%20Charlie%20Munger%20thinking%20framework%2C%20warm%20cognac%20tones%2C%20interconnected%20geometric%20nodes%2C%20abstract%20latticework%20design%2C%20clean%20educational%20art%2C%20no%20text&width=1200&height=800&seq=article-5&orientation=landscape',
    tags: ['思维模型', '查理·芒格', '精英策略'],
    featured: false
  },
  {
    id: '6',
    title: '刻意练习：普通人通往卓越的唯一路径',
    excerpt: '一万小时定律的真相与谬误，以及如何正确地进行刻意练习',
    content: `<p>安德斯·艾利克森的"刻意练习"理论改变了我们对天赋和努力的理解。但很多人误解了这个概念——<strong>不是所有的练习都叫刻意练习，也不是练够一万小时就能成为专家。</strong></p>

<h2>刻意练习的四个核心要素</h2>

<p><strong>1. 明确的目标：</strong>每次练习必须有一个清晰、具体的改进目标。不是"我要练好钢琴"，而是"今天我要把第12小节的音阶从120bpm提升到140bpm且不错音"。</p>

<p><strong>2. 高度专注：</strong>刻意练习需要极度的精神集中。每天的有效练习时间通常只有3-5小时，超过这个时间，注意力下降，练习质量断崖式下滑。</p>

<p><strong>3. 即时反馈：</strong>没有反馈的练习是盲目的。你需要教练、老师或至少是自我录像来发现问题和不足。</p>

<p><strong>4. 走出舒适区：</strong>刻意练习的核心是不断挑战略高于当前能力水平的目标。太简单会无聊，太难会挫败，<strong>"刚刚好难一点"</strong>才是最有效的学习区。</p>

<h2>一万小时定律的误读</h2>

<p>艾利克森从未说过"任何人练一万小时都能成为专家"。他的发现是：<strong>世界级专家普遍积累了大量刻意练习时间</strong>（大约一万小时）。但这里的"练习"特指刻意练习，而非随意的重复。</p>

<p>如果练习方法不对，练两万小时也成不了专家。反之，配合正确的刻意练习方法，即使时间不到一万小时，也能达到很高的水平。</p>`,
    category: 'gaoxiao-chuanshou',
    author: '秘密教学编辑部',
    date: '2026-07-28',
    readTime: '10分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20deliberate%20practice%20and%20skill%20mastery%2C%20warm%20bronze%20tones%2C%20ascending%20geometric%20steps%20representing%20improvement%2C%20clean%20educational%20art%20style%2C%20no%20text&width=1200&height=800&seq=article-6&orientation=landscape',
    tags: ['刻意练习', '一万小时', '高效传授'],
    featured: false
  },
  {
    id: '7',
    title: '番茄工作法进阶：不只是25分钟那么简单',
    excerpt: '从基础番茄钟到深度心流状态，重新认识这个被低估的效率工具',
    content: `<p>番茄工作法可能是最简单、也最被低估的时间管理方法。大多数人只知道"学25分钟，休息5分钟"，但这只是最表面的操作。<strong>真正的番茄工作法是一套完整的注意力管理哲学。</strong></p>

<h2>番茄工作法的核心哲学</h2>

<p>弗朗西斯科·西里洛在1980年代发明番茄工作法时，核心洞察是：<strong>人类的注意力是有限的，但可以通过结构化训练来延长和优化。</strong></p>

<p>一个番茄钟（25分钟）不仅仅是工作时间单位，它是：</p>
<ul>
<li>一个承诺——这段时间只做一件事</li>
<li>一个保护——任何打断都必须等到番茄钟结束</li>
<li>一个反馈——完成后你会获得微小但真实的成就感</li>
</ul>

<h2>进阶技巧：从番茄钟到心流</h2>

<p><strong>技巧一：动态调整时长。</strong>25分钟不是铁律。如果你发现自己在20分钟时就开始分心，缩短到20分钟。如果你能轻松完成25分钟，尝试延长到35或45分钟。</p>

<p><strong>技巧二：主题番茄。</strong>将连续的3-4个番茄钟分配在同一个主题上，形成深度工作块。这比分散在不同任务上的效果要好得多。</p>

<p><strong>技巧三：番茄日志。</strong>每天记录完成了几个番茄钟、被打断了几次、每次打断的原因。一周后回顾，你会发现自己的注意力模式，从而有针对性地改进。</p>`,
    category: 'yincang-jiqiao',
    author: '秘密教学编辑部',
    date: '2026-07-25',
    readTime: '8分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20pomodoro%20technique%20and%20deep%20focus%2C%20warm%20sand%20tones%2C%20abstract%20timer%20and%20flow%20state%20visualization%2C%20clean%20educational%20art%2C%20no%20text&width=1200&height=800&seq=article-7&orientation=landscape',
    tags: ['番茄工作法', '时间管理', '隐藏技巧'],
    featured: false
  },
  {
    id: '8',
    title: '康奈尔笔记法：一页纸改变你的信息吸收效率',
    excerpt: '不是笔记记得多就好，而是记得巧才重要',
    content: `<p>你是否有过这样的经历：上课拼命记笔记，下课后发现自己完全没听懂老师在讲什么？这是因为你的大脑资源被<strong>"记录"占据了，没留给"理解"。</strong></p>

<h2>康奈尔笔记法的三区结构</h2>

<p>将一页纸分成三个区域：</p>
<ul>
<li><strong>笔记区（右侧，占70%）：</strong>课堂上记录要点、关键词、核心概念</li>
<li><strong>线索区（左侧，占30%）：</strong>课后24小时内，提炼右侧内容为关键词和问题</li>
<li><strong>总结区（底部）：</strong>用2-3句话总结整页内容的核心要点</li>
</ul>

<h2>为什么康奈尔笔记法有效？</h2>

<p>这个结构的设计非常巧妙：</p>

<p>它强制你在课后<strong>主动处理信息</strong>——提炼关键词和问题本身就是一次主动回忆练习。底部总结逼迫你从全局角度思考本页内容的逻辑关系。</p>

<p>复习时，你只需遮住右侧笔记区，看着左侧的线索进行主动回忆。这比反复阅读笔记要<strong>高效3-5倍</strong>。</p>

<h2>进阶用法：康奈尔笔记+费曼技巧</h2>

<p>将两者结合会产生奇妙的化学反应：先在笔记区记录要点，在复习时用线索区的关键词尝试向他人（或想象中的听众）解释整个概念。卡住的地方就是知识缺口，马上回补。</p>`,
    category: 'yincang-jiqiao',
    author: '秘密教学编辑部',
    date: '2026-07-22',
    readTime: '7分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20Cornell%20note%20taking%20method%2C%20warm%20sepia%20tones%2C%20structured%20layout%20with%20three%20sections%2C%20clean%20educational%20art%20style%2C%20no%20text&width=1200&height=800&seq=article-8&orientation=landscape',
    tags: ['康奈尔笔记', '笔记方法', '隐藏技巧'],
    featured: false
  },
  {
    id: '9',
    title: '苏格拉底式提问：用问题引导深度思考的艺术',
    excerpt: '最好的老师不给你答案，而是教你问对问题',
    content: `<p>苏格拉底从未写下一本书，但他的教学方法影响了整个西方文明。他的秘密武器？<strong>提问。</strong>不是随便提问，而是一套系统性的、层层递进的提问方法。</p>

<h2>苏格拉底式提问的六个层次</h2>

<p><strong>1. 澄清性问题：</strong>"你说的这个观点具体是什么意思？能举个例子吗？"</p>

<p><strong>2. 探究假设：</strong>"你的论证建立在什么前提假设之上？这些假设成立吗？"</p>

<p><strong>3. 探究证据：</strong>"有什么证据支持你的观点？这些证据的来源可靠吗？"</p>

<p><strong>4. 考虑不同视角：</strong>"有没有其他可能的解释？反对者会怎么说？"</p>

<p><strong>5. 探究后果：</strong>"如果这个观点成立，会带来什么影响和后果？"</p>

<p><strong>6. 反思问题本身：</strong>"我们为什么要问这个问题？这个问题本身有没有问题？"</p>

<h2>如何将苏格拉底式提问用于自学？</h2>

<p>学完一个新的知识点后，不要问自己"我记住了吗？"，而要问：<strong>"这个知识成立的前提是什么？在什么情况下它不成立？它和之前学的知识有什么联系和矛盾？"</strong></p>

<p>这种提问方式会把你的思维从"接受模式"切换到<strong>"检验模式"</strong>——你不再被动地吸收信息，而是主动地审查、挑战和整合知识。</p>`,
    category: 'hexin-fangfa',
    author: '秘密教学编辑部',
    date: '2026-07-20',
    readTime: '9分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20Socratic%20questioning%20method%2C%20warm%20honey%20tones%2C%20layered%20question%20marks%20forming%20a%20spiral%2C%20philosophical%20art%20style%2C%20no%20text&width=1200&height=800&seq=article-9&orientation=landscape',
    tags: ['苏格拉底式提问', '深度思考', '核心方法'],
    featured: false
  },
  {
    id: '10',
    title: '元认知：学会"思考你的思考"',
    excerpt: '所有高效学习法的底层操作系统——元认知能力',
    content: `<p>如果所有学习方法是一个操作系统，那么<strong>元认知就是这个操作系统的内核。</strong>元认知，简单说就是"对思考的思考"——你不仅在学习，还在监控和调节自己的学习过程。</p>

<h2>元认知的三大组成部分</h2>

<p><strong>1. 元认知知识：</strong>你知道自己知道什么、不知道什么。大多数学习者的问题在于：他们<strong>不知道自己不知道什么</strong>。</p>

<p><strong>2. 元认知监控：</strong>在学习过程中实时判断"我理解了吗？""我需要换一种方法吗？""我的注意力还在吗？"</p>

<p><strong>3. 元认知调节：</strong>根据监控结果调整策略。发现没理解就停下来重新学习，注意力不集中就切换环境或休息。</p>

<h2>如何训练元认知？</h2>

<p><strong>学习日志：</strong>每天花5分钟记录：今天学了什么？用了什么方法？效果如何？下次可以怎么改进？</p>

<p><strong>"三问"自查法：</strong>每学完一个段落，问自己三个问题：我刚才读的内容核心是什么？我能用自己的话复述吗？这个知识和我知道的其他知识有什么联系？</p>

<p><strong>思维出声法：</strong>在解决复杂问题时，把思考过程说出来。这迫使你外化内部思维过程，从而更容易发现逻辑漏洞。</p>`,
    category: 'shendu-miji',
    author: '秘密教学编辑部',
    date: '2026-07-18',
    readTime: '10分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20metacognition%20and%20thinking%20about%20thinking%2C%20warm%20amber%20and%20cream%20tones%2C%20mirror%20reflecting%20brain%2C%20abstract%20self%20awareness%20concept%2C%20clean%20educational%20art%2C%20no%20text&width=1200&height=800&seq=article-10&orientation=landscape',
    tags: ['元认知', '深度学习', '底层系统'],
    featured: false
  },
  {
    id: '11',
    title: '心流状态：如何进入最佳学习体验',
    excerpt: '当学习变得像呼吸一样自然——心流状态的触发条件与训练方法',
    content: `<p>你有没有过这样的体验：完全沉浸在某个任务中，时间飞速流逝，你甚至忘记了吃饭？这就是<strong>心流状态</strong>——心理学家米哈里·契克森米哈伊称之为"人类体验中最接近幸福的状态"。</p>

<h2>心流的九个特征</h2>

<p>1. 挑战与技能的平衡——任务难度正好匹配你的能力<br/>
2. 行动与意识的融合——不需要刻意"想"，自然而然地做<br/>
3. 清晰的目标——每一刻都知道该做什么<br/>
4. 即时反馈——你能立刻知道自己做对了还是做错了<br/>
5. 高度专注——无关信息自动被过滤<br/>
6. 掌控感——你感觉到自己能控制局面<br/>
7. 自我意识的消失——不再担心"别人怎么看我"<br/>
8. 时间感扭曲——时间似乎变快或变慢<br/>
9. 内在驱动——做这件事本身就是回报</p>

<h2>如何主动触发心流？</h2>

<p><strong>难度匹配是关键。</strong>如果任务难度远高于你的能力，你会焦虑；如果远低于，你会无聊。心流的"甜蜜点"在<strong>难度略高于当前能力的4%左右</strong>。</p>

<p><strong>消除打断。</strong>心流状态需要连续的不被打断的时间块。把手机放到另一个房间，关闭通知，告诉周围人不要打扰。</p>

<p><strong>设定明确的小目标。</strong>不要模糊地"我要学习"，而是"我接下来30分钟要把这三个概念之间的关系画清楚"。</p>`,
    category: 'shendu-miji',
    author: '秘密教学编辑部',
    date: '2026-07-15',
    readTime: '8分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20flow%20state%20and%20optimal%20learning%20experience%2C%20warm%20gold%20tones%2C%20abstract%20flowing%20water%20representing%20effortless%20focus%2C%20clean%20minimalist%20art%2C%20no%20text&width=1200&height=800&seq=article-11&orientation=landscape',
    tags: ['心流', '最佳体验', '深度学习'],
    featured: false
  },
  {
    id: '12',
    title: '交叉学习法：为什么混着学比集中学更有效',
    excerpt: '打破"一个一个学完"的思维定式，交叉训练带来意想不到的效果',
    content: `<p>传统的学习建议是：先学完A，再学B，再学C。但认知科学的最新研究表明：<strong>把A、B、C交叉混合学习，效果反而更好。</strong>这被称为"交叉学习法"。</p>

<h2>交叉学习的科学原理</h2>

<p>当你集中学习一个主题时，大脑很快找到规律，进入自动模式——但这是<strong>假性掌握</strong>。在真实场景中，你需要判断"这个问题属于哪种类型？应该用什么方法？"集中学习绕过了这个最重要的判断环节。</p>

<p>交叉学习迫使大脑在每次切换时重新判断和选择策略，这恰恰强化了<strong>辨别能力和策略选择能力</strong>——这两个能力在实际应用中至关重要。</p>

<h2>如何实践交叉学习？</h2>

<p><strong>数学学习：</strong>不要做完所有加法题再做减法题。把不同类型的问题混在一起，迫使自己判断每道题该用什么方法。</p>

<p><strong>语言学习：</strong>不要学完所有词汇再学语法。词汇、语法、听力、口语交叉进行。今天学30分钟词汇+30分钟语法，比一天全学词汇、另一天全学语法效果好得多。</p>

<p><strong>技能学习：</strong>学吉他时，不要练完所有和弦再练指法。把不同类型的练习混在一起。</p>`,
    category: 'gaoxiao-chuanshou',
    author: '秘密教学编辑部',
    date: '2026-07-12',
    readTime: '7分钟',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20interleaved%20learning%20method%2C%20warm%20sand%20and%20copper%20tones%2C%20interwoven%20threads%20representing%20mixed%20learning%20topics%2C%20clean%20educational%20art%2C%20no%20text&width=1200&height=800&seq=article-12&orientation=landscape',
    tags: ['交叉学习', '混学策略', '高效传授'],
    featured: false
  }
];

export const featuredArticles = articles.filter(a => a.featured);

export function getArticlesByCategory(slug: string): Article[] {
  return articles.filter(a => a.category === slug);
}

export function getArticleById(id: string): Article | undefined {
  return articles.find(a => a.id === id);
}

export function getRelatedArticles(article: Article, count: number = 3): Article[] {
  return articles
    .filter(a => a.id !== article.id && a.category === article.category)
    .slice(0, count);
}